import React, { useState, useContext } from 'react';
import { Box, Text, Flex, Image, Input, Button } from '@chakra-ui/react';
import { useForm } from 'react-hook-form';

import loginImg from '../../assets/images/login.svg';
import { CustomInput, InputError } from '../../components/common/form/Input';
import { LockIcon, MailIcon } from '../../components/login';
import { SolidBtn } from '../../components/common/buttons';
import { Link } from 'react-router-dom';
import { AuthContext, LOGIN } from '../../context/auth';
import { toast } from 'react-toastify';
import { adminLogin } from '../../queries';
import { ReactComponent as ShowPassword } from '../../assets/icons/show.svg';

function Login() {
  const { dispatch } = useContext(AuthContext);
  const [isLoading, setIsLoading] = useState(false);
  const { register, handleSubmit, errors } = useForm();
  const [passwordInputType, setpasswordInputType] = useState('password');
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaToken, setMfaToken] = useState('');
  const [useBackupCode, setUseBackupCode] = useState(false);
  const [backupCode, setBackupCode] = useState('');
  const [credentials, setCredentials] = useState(null);

  const onSubmit = async (inputData) => {
    try {
      setIsLoading(true);
      let loginData = { ...inputData };

      if (mfaRequired) {
        if (useBackupCode) {
          loginData = { ...credentials, backupCode };
        } else {
          loginData = { ...credentials, token: mfaToken };
        }
      }

      const { data } = await adminLogin(loginData);
      dispatch({ type: LOGIN, payload: data });
      toast('Login successful');
      window.location.replace('/admin/overview');
    } catch (error) {
      const message = error.response?.data?.message || 'Login Failed';
      const isMfaRequired = error.response?.data?.data?.mfaRequired;

      if (isMfaRequired && !mfaRequired) {
        setMfaRequired(true);
        setCredentials(inputData);
        toast.info('Please enter your 2FA code');
        setIsLoading(false);
        return;
      }

      if (error.response?.status === 429) {
        toast.error('Too many login attempts. Please try again in 15 minutes.');
      } else {
        toast.error(message);
      }
      setIsLoading(false);
    }
  };

  const handleMfaTokenChange = (e) => {
    const val = e.target.value.replace(/\D/g, '');
    if (val.length <= 6) {
      setMfaToken(val);
    }
  };

  const handleBackupCodeChange = (e) => {
    setBackupCode(e.target.value);
  };

  return (
    <Flex align="center" w="100%" h="100vh" overflow="hidden">
      <Flex
        justify="center"
        align="flex-end"
        bg="brand.blue"
        w="55%"
        h="100%"
        color="white"
        pt="7.4rem"
        pl="5rem"
      >
        <Flex direction="column" justify="space-between" w="100%" maxW="690px">
          <Box mb="5rem" ml="2%">
            <Text lineHeight="4.3rem" fontSize="3.8rem" fontFamily="bold">
              Virtual airtime <br /> and data recharge <br /> made easy.
            </Text>
            <Text
              lineHeight="2.4rem"
              mt="2.3rem"
              fontSize="2rem"
              fontFamily="regular"
            >
              Awake is a mobile virtual credit company focused <br /> on
              bridging the gap between the telcos and <br /> businesses in
              Nigeria.
            </Text>
          </Box>
          <Box w="100%">
            <Image src={loginImg} w="100%" maxW="100%" />
          </Box>
        </Flex>
      </Flex>
      <Flex w="45%" h="100%" justify="center" pos="relative">
        <Box
          borderBottom="1px solid #EFEFEF"
          textAlign="right"
          py="2rem"
          pr="2rem"
          pos="absolute"
          top="0"
          w="100%"
        >
          <Text
            as="span"
            color="#939AA3"
            letterSpacing="-0.02em"
            fontFamily="medium"
            mr="1.2rem"
          >
            Sign in as a merchant ?
          </Text>
          <Text
            as={Link}
            to="/merchant/login"
            fontFamily="medium"
            color="brand.blue"
            letterSpacing="-0.02em"
          >
            Sign in
          </Text>
        </Box>
        <Box as="form" mt="18.4rem" onSubmit={handleSubmit(onSubmit)}>
          <Text fontSize="2.8rem" fontFamily="regular" color="brand.blue">
            Sign In
          </Text>
          <Text fontFamily="bold" mt="4rem" fontSize="2.8rem" color="#212D43">
            Welcome, Admin
          </Text>
          <Text w="80%" fontFamily="regular" color="#212D43">
            {mfaRequired
              ? 'Enter your two-factor authentication code to continue.'
              : 'You can now login into your dashboard with your email and password.'}
          </Text>

          {!mfaRequired && (
            <>
              <CustomInput
                id="email"
                label="Email"
                name="email"
                inputRef={register({
                  required: 'Email is required',
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Invalid email address',
                  },
                })}
              >
                <MailIcon />
              </CustomInput>
              {errors.email && <InputError text={errors.email.message} />}
              <CustomInput
                id="password"
                label="Password"
                type={passwordInputType}
                name="password"
                style={{ marginTop: '2rem' }}
                inputRef={register({
                  required: 'Password is required',
                })}
                rightAddon={
                  <ShowPassword
                    onClick={() =>
                      passwordInputType === 'password'
                        ? setpasswordInputType('text')
                        : setpasswordInputType('password')
                    }
                  />
                }
              >
                <LockIcon />
              </CustomInput>
              {errors.password && <InputError text={errors.password.message} />}
            </>
          )}

          {mfaRequired && (
            <Box mt="2rem">
              <Text
                fontSize="14px"
                fontFamily="medium"
                color="#444"
                mb="0.5rem"
              >
                {useBackupCode ? 'Backup Code' : 'Authenticator Code'}
              </Text>
              {useBackupCode ? (
                <Input
                  value={backupCode}
                  onChange={handleBackupCodeChange}
                  placeholder="XXXX-XXXX-XXXX"
                  fontSize="16px"
                  h="50px"
                  textTransform="uppercase"
                  borderColor="#E0E0E0"
                  _hover={{ borderColor: '#BDBDBD' }}
                  _focus={{
                    borderColor: '#1A73E8',
                    boxShadow: '0 0 0 1px #1A73E8',
                  }}
                />
              ) : (
                <Input
                  value={mfaToken}
                  onChange={handleMfaTokenChange}
                  placeholder="000000"
                  maxLength={6}
                  fontSize="24px"
                  letterSpacing="0.5rem"
                  textAlign="center"
                  fontFamily="bold"
                  h="60px"
                  borderColor="#E0E0E0"
                  _hover={{ borderColor: '#BDBDBD' }}
                  _focus={{
                    borderColor: '#1A73E8',
                    boxShadow: '0 0 0 1px #1A73E8',
                  }}
                />
              )}
              <Button
                variant="link"
                color="brand.blue"
                fontSize="14px"
                mt="1rem"
                onClick={() => {
                  setUseBackupCode(!useBackupCode);
                  setMfaToken('');
                  setBackupCode('');
                }}
              >
                {useBackupCode
                  ? 'Use authenticator code instead'
                  : 'Use backup code instead'}
              </Button>
            </Box>
          )}

          <SolidBtn
            type="submit"
            mt="3.2rem"
            isLoading={isLoading}
            isDisabled={
              mfaRequired &&
              ((!useBackupCode && mfaToken.length !== 6) ||
                (useBackupCode && !backupCode.trim()))
            }
          >
            Sign in
          </SolidBtn>
          <Text
            as={Link}
            to="/admin/forgot-password"
            display="block"
            color="brand.blue"
            fontFamily="regular"
            mt="2.4rem"
          >
            I forgot my password
          </Text>
        </Box>
      </Flex>
    </Flex>
  );
}

export { Login };
